// Tracks the visualViewport bottom offset so a fixed-position element (the
// chat input) can stay above the iOS / Android software keyboard. When the
// keyboard is hidden the offset is 0; when it's visible the offset is the
// keyboard's covered height (window.innerHeight - viewport.height -
// viewport.offsetTop).
//
// On platforms without visualViewport support (older Android browsers) we
// return 0, which keeps the element at the bottom of the layout viewport —
// the same as the current behavior, no regression.
//
// MOB9 — on iPhone PWA standalone with `viewport-fit=cover`,
// `window.innerHeight` includes the home-indicator safe-area but `vv.height`
// does not. The naive `innerHeight - vv.height - offsetTop` formula returned
// ~34 px on iPhone Pro PWA even when the keyboard was closed, because that
// delta IS the home-indicator safe-area. The composer's `paddingBottom` then
// double-counted `env(safe-area-inset-bottom)`, producing a tall
// `bg-secondary` band below the toolbar.
//
// Fix: only treat the delta as a keyboard offset when an input is actually
// focused. Without focus, no keyboard is visible, so any delta is a CSS
// layout artefact and we return 0. `focusin` / `focusout` listeners keep the
// offset reactive when the user focuses the textarea while the keyboard is
// already animating in.

import { useEffect, useState } from 'react';

function isInputFocused(): boolean {
  if (typeof document === 'undefined') return false;
  const el = document.activeElement;
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'TEXTAREA' || tag === 'INPUT';
}

export function useVisualViewport(): { keyboardOffset: number } {
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const vv = window.visualViewport;
    if (!vv) return;

    const update = () => {
      if (!isInputFocused()) {
        setOffset(0);
        return;
      }
      const o = Math.max(
        0,
        window.innerHeight - vv.height - vv.offsetTop,
      );
      setOffset(o);
    };

    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    document.addEventListener('focusin', update);
    document.addEventListener('focusout', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
      document.removeEventListener('focusin', update);
      document.removeEventListener('focusout', update);
    };
  }, []);

  return { keyboardOffset: offset };
}
